import { useEffect, useState } from 'react';
import { videoPlayer } from 'cloudinary-video-player';
import { format, isValid } from 'date-fns';
import { CLOUDINARY_CLOUD_NAME } from '@/config';
import { useToastContext } from '@/contexts/toastContext';
import { SeminarContentType } from '@/services/seminars';
import * as Styles from './SeminarVideo.styles';

import 'cloudinary-video-player/dist/cld-video-player.light.min.css';

type SeminarVideoType = Pick<SeminarContentType, 'url' | 'name' | 'createdAt' | 'thumbnail'>;

const videoElementId = 'video-player';
const SeminarVideo = ({ url, name, createdAt, thumbnail }: SeminarVideoType): JSX.Element => {
  const [isDisplayingThumbnail, setIsDisplayingThumbnail] = useState(!!thumbnail);
  const toast = useToastContext();

  useEffect(() => {
    try {
      const videoPlayerRef = videoPlayer(videoElementId, {
        controls: true,
        cloud_name: CLOUDINARY_CLOUD_NAME,
        bigPlayButton: true,
        innerWidth: 20,
        autoplay: false,
        fluid: true,
        showJumpControls: false,
      });

      videoPlayerRef.source(url);
    } catch (err) {
      toast.setSeverity('error');
      toast.setMessage('It was not possible to load the video. Please, try later.');
      toast.showToast();
    }
  }, [url, toast]);

  const onPlayHandler = (): void => {
    setIsDisplayingThumbnail(false);
  };

  const isDateValid = isValid(new Date(createdAt));
  const publishedDate = isDateValid && format(new Date(createdAt), 'MMM dd p');

  return (
    <div data-testid="seminar-details-video">
      <Styles.VideoWrapper>
        {isDisplayingThumbnail && (
          <Styles.Thumbnail data-testid="seminar-details-thumbnail" alt="" src={thumbnail} />
        )}
        <Styles.Video
          onPlay={onPlayHandler}
          id={videoElementId}
          controls
          className="cld-video-player cld-video-player-skin-dark"
        />
      </Styles.VideoWrapper>
      <Styles.VideoTitle data-testid="seminar-details-name">{name}</Styles.VideoTitle>
      {isDateValid && (
        <Styles.VideoPublishedAt data-testid="seminar-details-date">
          {publishedDate}
        </Styles.VideoPublishedAt>
      )}
    </div>
  );
};

export default SeminarVideo;
